import { useMemo } from "react";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { resolveUiColor } from "../utils/colors";
import type { SegmentProps } from "./Segment.types";

type StyleProps = {
  $bg: string;
  $boxShadow: string;
  $margin: string;
  $padding: string;
  $borderRadius: string;
  $border: string;
  $borderTop: string;
  $color: string;
  $textAlign: string;
};

const StyledSegment = styled.div<StyleProps>`
  position: relative;
  background: ${({ $bg }) => $bg};
  box-shadow: ${({ $boxShadow }) => $boxShadow};
  margin: ${({ $margin }) => $margin};
  padding: ${({ $padding }) => $padding};
  border-radius: ${({ $borderRadius }) => $borderRadius};
  border: ${({ $border }) => $border};
  border-top: ${({ $borderTop }) => $borderTop};
  color: ${({ $color }) => $color};
  text-align: ${({ $textAlign }) => $textAlign};
  box-sizing: border-box;

  &:first-child {
    margin-top: 0;
  }

  &.is-fluid {
    width: 100%;
  }

  &.is-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    max-width: initial;
    min-height: 18rem;
    animation: none;
    overflow: visible;

    button,
    textarea {
      display: block;
    }

    & > input,
    button,
    textarea,
    [class*="field"] {
      max-width: 15rem;
      margin-left: auto;
      margin-right: auto;
    }

    & > .inline {
      align-self: center;

      & > button {
        display: inline-block;
        width: auto;
        margin: 0 0.35714286rem 0 0;

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  &.is-loading::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 100;
    border-radius: inherit;
  }

  &.is-loading::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -1.2em 0 0 -1.2em;
    width: 2.4em;
    height: 2.4em;
    border-radius: 500rem;
    border: 0.2em solid rgba(0, 0, 0, 0.1);
    border-top-color: #767676;
    animation: sdo-segment-spin 0.6s linear infinite;
    z-index: 101;
    box-sizing: border-box;
  }

  @keyframes sdo-segment-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

function segmentBoxShadow(
  basic?: boolean,
  raised?: boolean,
  attached?: boolean | "top" | "bottom",
): string {
  if (basic || attached) {
    return "none";
  }
  if (raised) {
    return "0 2px 4px 0 rgba(34, 36, 38, 0.12), 0 2px 10px 0 rgba(34, 36, 38, 0.15)";
  }
  return "0 1px 2px 0 rgba(34, 36, 38, 0.15)";
}

function segmentMargin(attached?: boolean | "top" | "bottom"): string {
  if (attached === "top") {
    return "1rem 0 0";
  }
  if (attached === "bottom") {
    return "0 0 1rem";
  }
  if (attached === true) {
    return "0";
  }
  return "1rem 0";
}

function segmentPadding(
  basic?: boolean,
  padded?: boolean | "very",
  placeholder?: boolean,
): string {
  if (basic) {
    return "0";
  }
  if (placeholder) {
    return "1em";
  }
  if (padded === "very") {
    return "3em";
  }
  if (padded) {
    return "1.5em";
  }
  return "1em";
}

function segmentBorderRadius(attached?: boolean | "top" | "bottom"): string {
  if (attached === "top") {
    return "0.28571429rem 0.28571429rem 0 0";
  }
  if (attached === "bottom") {
    return "0 0 0.28571429rem 0.28571429rem";
  }
  if (attached === true) {
    return "0";
  }
  return "0.28571429rem";
}

const SegmentComponent = ({
  basic,
  secondary,
  tertiary,
  inverted,
  loading,
  padded,
  raised,
  color,
  attached,
  placeholder,
  textAlign,
  fluid,
  children,
  className,
  style,
  onClick,
}: SegmentProps) => {
  const theme = useTheme();
  const accent = useMemo(() => resolveUiColor(theme, color), [theme, color]);

  let bg = "#ffffff";
  if (placeholder) {
    bg = "#f9fafb";
  } else if (inverted) {
    bg = "#1b1c1d";
  } else if (tertiary) {
    bg = "#fbfbfb";
  } else if (secondary) {
    bg = "#f3f4f5";
  }

  const border = basic ? "none" : "1px solid rgba(34, 36, 38, 0.15)";
  let borderTop = border;
  if (basic) {
    borderTop = "none";
  } else if (accent) {
    borderTop = `2px solid ${accent}`;
  } else if (attached === "bottom" || attached === true) {
    borderTop = "none";
  }

  const boxShadow = placeholder
    ? "0 2px 25px 0 rgba(34, 36, 38, 0.05) inset"
    : segmentBoxShadow(basic, raised, attached);

  return (
    <StyledSegment
      className={cx(
        fluid && "is-fluid",
        placeholder && "is-placeholder",
        loading && "is-loading",
        className,
      )}
      style={style}
      onClick={onClick}
      $bg={bg}
      $boxShadow={boxShadow}
      $margin={segmentMargin(attached)}
      $padding={segmentPadding(basic, padded, placeholder)}
      $borderRadius={segmentBorderRadius(attached)}
      $border={border}
      $borderTop={borderTop}
      $color={inverted ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)"}
      $textAlign={textAlign === "justified" ? "justify" : textAlign || "left"}
    >
      {children}
    </StyledSegment>
  );
};

export const Segment = styled(SegmentComponent)``;
